import { useEffect, useMemo, useState } from 'react'
import {
  analyzeSeo, renderTitleTemplate, serpTruncate, textPixels,
  SEO_GROUP_LABEL, SEO_LIMITS,
  type CheckStatus, type SeoCheck, type SeoGroup, type SeoInput, type SeoResult, type SiteSettings,
} from '@nhanho/shared'
import type { Media, SeoMeta } from '@nhanho/shared'
import { seo as seoApi, type SeoConflicts } from '../../lib/api'
import { useDebounced } from '../../lib/useDebounced'
import { Field } from '../ui/Field'
import { Input, Textarea } from '../ui/Input'
import { Toggle } from '../ui/Toggle'
import { Badge } from '../ui/Badge'
import { TagInput } from '../ui/TagInput'
import { ImageField } from '../media/ImageField'

/** Giá trị khối SEO trong form — khớp `SeoMetaBody` khi gửi lên API. */
export interface SeoValue {
  title: string
  description: string
  focusKeyword: string
  image: Media | null
  secondaryKeywords: string[]
  canonical: string
  noindex: boolean
  nofollow: boolean
  ogTitle: string
  ogDescription: string
  /** JSON-LD tự nhập — **chuỗi thô** đúng như người dùng dán, không parse. */
  jsonLd: string
}

export const EMPTY_SEO: SeoValue = {
  title: '', description: '', focusKeyword: '', image: null,
  secondaryKeywords: [], canonical: '', noindex: false, nofollow: false, ogTitle: '', ogDescription: '',
  jsonLd: '',
}

/**
 * `meta` từ API → giá trị của form. Ba form (sản phẩm, bài viết, danh mục) đều cần đúng
 * phép biến đổi này; trước đây nó được chép tay ở từng form và đã lệch nhau là chuyện
 * sớm muộn. Chỗ khác biệt duy nhất so với passthrough là `image`: API trả object đã
 * populate, body gửi lên chỉ nhận id.
 */
export function seoFromMeta(meta?: SeoMeta | null): SeoValue {
  return {
    title: meta?.title ?? '',
    description: meta?.description ?? '',
    focusKeyword: meta?.focusKeyword ?? '',
    image: typeof meta?.image === 'object' ? (meta.image as Media | null) : null,
    secondaryKeywords: meta?.secondaryKeywords ?? [],
    canonical: meta?.canonical ?? '',
    noindex: meta?.noindex ?? false,
    nofollow: meta?.nofollow ?? false,
    ogTitle: meta?.ogTitle ?? '',
    ogDescription: meta?.ogDescription ?? '',
    jsonLd: meta?.jsonLd ?? '',
  }
}

/** Giá trị form → body gửi lên API. */
export function seoToMeta(v: SeoValue) {
  return {
    title: v.title, description: v.description, focusKeyword: v.focusKeyword,
    image: v.image?.id ?? null, secondaryKeywords: v.secondaryKeywords,
    canonical: v.canonical, noindex: v.noindex, nofollow: v.nofollow,
    ogTitle: v.ogTitle, ogDescription: v.ogDescription,
    jsonLd: v.jsonLd,
  }
}

/**
 * Kết quả kiểm một chuỗi JSON-LD: lỗi để báo, và danh sách `@type` để người dán **nhìn
 * thấy Google sẽ đọc ra cái gì**. "JSON hợp lệ" chưa trả lời được câu hỏi thật của người
 * biên tập; "đã nhận: Recipe, Organization" thì có.
 */
function inspectJsonLd(raw: string): { error: string | null; types: string[] } {
  const text = raw.trim()
  if (!text) return { error: null, types: [] }
  if (/<\/script/i.test(text)) return { error: 'Không được chứa thẻ </script>', types: [] }
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (error) {
    return { error: `JSON không hợp lệ: ${error instanceof Error ? error.message : 'không đọc được'}`, types: [] }
  }
  const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
  const items = isObject(parsed) ? [parsed] : Array.isArray(parsed) && parsed.length > 0 && parsed.every(isObject) ? (parsed as Record<string, unknown>[]) : null
  if (!items) return { error: 'JSON-LD phải là một object {…} hoặc mảng object', types: [] }
  const types = items.map((o) => (typeof o['@type'] === 'string' ? (o['@type'] as string) : '(thiếu @type)'))
  return { error: null, types }
}

/**
 * Ô dán JSON-LD tự đặt — dùng ở tab SEO của mọi loại nội dung **và** ở Cài đặt SEO cho
 * trang chủ.
 *
 * Báo lỗi ngay khi gõ nhưng **không chặn nút Lưu**: cổng kiểm thật là API
 * (`lib/seo-body.ts`), và chặn ở client chỉ khiến người ta mất nguyên khối vừa dán khi
 * hai bên bất đồng. Đây là bản sao có chủ ý của luật phía API — lệch nhau thì admin báo
 * sai, còn dữ liệu vẫn đúng.
 */
export function JsonLdField({ value, onChange, error }: { value: string; onChange: (v: string) => void; error?: string }) {
  const { error: syntax, types } = useMemo(() => inspectJsonLd(value), [value])
  return (
    <Field
      label="Schema JSON-LD tuỳ chỉnh"
      hint="Dán nguyên khối JSON (không kèm thẻ <script>). Nối thêm vào các schema tự động, không thay thế chúng."
      error={error}
    >
      <Textarea
        value={value}
        rows={6}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "HowTo",\n  "name": "…"\n}'}
        className="font-mono text-xs"
      />
      {syntax ? (
        <p className="mt-1 text-xs text-danger">{syntax}</p>
      ) : types.length > 0 ? (
        <p className="mt-1 text-xs text-success">Hợp lệ — Google sẽ đọc: {types.join(', ')}</p>
      ) : null}
    </Field>
  )
}

const DOT: Record<CheckStatus, string> = { good: 'bg-success', warn: 'bg-warning', bad: 'bg-danger' }
const TEXT: Record<CheckStatus, string> = { good: 'text-success', warn: 'text-warning', bad: 'text-danger' }

/**
 * Thanh đo độ dài theo **pixel** — vì Google cắt tiêu đề/mô tả theo chiều rộng thật,
 * không theo số ký tự. Vùng xanh = an toàn, vượt vạch = bị cắt bằng "…".
 */
export function PixelMeter({ px, min, max }: { px: number; min: number; max: number }) {
  const pct = Math.min(100, (px / max) * 100)
  const state: CheckStatus = px === 0 ? 'bad' : px > max ? 'warn' : px < min ? 'warn' : 'good'
  const color = state === 'good' ? 'bg-success' : state === 'warn' ? 'bg-warning' : 'bg-danger'
  return (
    <div className="mt-1 flex items-center gap-2">
      <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-border">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
        <span className="absolute inset-y-0" style={{ left: `${(min / max) * 100}%`, borderLeft: '1px dashed rgba(0,0,0,.25)' }} />
      </div>
      <span className={`shrink-0 text-2xs ${TEXT[state]}`}>{px}/{max}px</span>
    </div>
  )
}

/** Xem trước kết quả Google, cắt đúng như SERP thật. */
export function SerpPreview({
  title, description, url, device,
}: { title: string; description: string; url: string; device: 'desktop' | 'mobile' }) {
  const t = serpTruncate(title || 'Tiêu đề trang', SEO_LIMITS.titlePixels.max, 20)
  const d = serpTruncate(description || 'Chưa có mô tả — Google sẽ tự lấy một đoạn bất kỳ trong nội dung.', device === 'mobile' ? 680 : SEO_LIMITS.descPixels.max, 14)
  return (
    <div className={`rounded-card-sm border border-border bg-white p-4 ${device === 'mobile' ? 'max-w-sm' : ''}`}>
      <p className="truncate text-xs text-[#4d5156]">{url}</p>
      <p className="mt-0.5 text-lg leading-snug text-[#1a0dab]">{t.text}</p>
      <p className="mt-0.5 text-sm leading-snug text-[#4d5156]">{d.text}</p>
      {(t.truncated || d.truncated) && (
        <p className="mt-2 text-2xs text-warning">
          {t.truncated && 'Tiêu đề bị cắt. '}{d.truncated && 'Mô tả bị cắt.'}
        </p>
      )}
    </div>
  )
}

/** Xem trước thẻ chia sẻ Facebook/Zalo (Open Graph). */
export function SocialPreview({ image, title, description, host }: { image: Media | null; title: string; description: string; host: string }) {
  return (
    <div className="max-w-md overflow-hidden rounded-card-sm border border-border bg-white">
      <div className="aspect-[1200/630] bg-sand">
        {image ? <img src={image.sizes?.hero?.url ?? image.url} alt={image.alt} className="size-full object-cover" /> : (
          <div className="grid size-full place-items-center text-xs text-ink-faint">Chưa có ảnh chia sẻ — mạng xã hội sẽ lấy ảnh bất kỳ</div>
        )}
      </div>
      <div className="border-t border-border p-3">
        <p className="text-2xs uppercase tracking-wider text-ink-faint">{host}</p>
        <p className="mt-0.5 line-clamp-2 text-sm font-medium text-ink">{title || 'Tiêu đề khi chia sẻ'}</p>
        <p className="line-clamp-2 text-xs text-ink-muted">{description || 'Mô tả khi chia sẻ'}</p>
      </div>
    </div>
  )
}

function ScoreRing({ result }: { result: SeoResult }) {
  const tone = result.rating === 'good' ? 'text-success' : result.rating === 'ok' ? 'text-warning' : 'text-danger'
  const label = result.rating === 'good' ? 'Tốt' : result.rating === 'ok' ? 'Tạm được' : 'Cần sửa'
  const counts = result.checks.reduce<Record<CheckStatus, number>>((a, c) => ({ ...a, [c.status]: a[c.status] + 1 }), { good: 0, warn: 0, bad: 0 })
  return (
    <div className="flex items-center gap-4">
      <div className={`font-heading text-4xl font-bold ${tone}`}>{result.score}</div>
      <div className="text-xs">
        <div className={`font-medium ${tone}`}>{label}</div>
        <div className="text-ink-muted">{counts.good} đạt · {counts.warn} nên sửa · {counts.bad} lỗi</div>
      </div>
    </div>
  )
}

function CheckRow({ c }: { c: SeoCheck }) {
  return (
    <li className="flex gap-2 text-xs">
      <span className={`mt-1 size-2 shrink-0 rounded-full ${DOT[c.status]}`} />
      <div>
        <div className="text-ink">
          {c.label}
          {c.weight === 3 && c.status !== 'good' && <Badge tone="danger">quan trọng</Badge>}
        </div>
        {c.status !== 'good' && <div className="mt-0.5 text-ink-muted">{c.hint}</div>}
      </div>
    </li>
  )
}

/**
 * Panel SEO đầy đủ cho sản phẩm và bài viết.
 *
 * Ba việc nó làm mà một ô "meta description" đơn thuần không làm được:
 *   1. **Xem trước thật**: cắt title/description theo pixel như Google, và thẻ OG như Facebook.
 *   2. **Chấm điểm theo trọng số** trên nội dung thật (HTML từ Tiptap): từ khoá trong
 *      H1/heading/đoạn mở đầu, mật độ, số từ, internal link, alt ảnh, câu dài…
 *   3. **Phát hiện trùng lặp toàn site** qua `GET /api/seo/conflicts` — trùng tiêu đề và
 *      trùng từ khoá (keyword cannibalization) là thứ không thể thấy khi chỉ nhìn một trang.
 */
export function SeoPanel({
  value, onChange, pageTitle, slug, html = '', text, kind, errors = {}, settings, docId, extra,
}: {
  value: SeoValue
  onChange: (v: SeoValue) => void
  /** Tiêu đề H1 (tên sản phẩm / bài viết / danh mục). */
  pageTitle: string
  /** Đường dẫn không có domain, vd `san-pham/moc-khoa-cao`. */
  slug: string
  /** HTML nội dung để phân tích. Danh mục không có — dùng `text`. */
  html?: string
  /**
   * Nội dung dạng **text thuần**, dùng khi không có HTML (mô tả danh mục là Textarea,
   * không phải Tiptap). `analyzeSeo` chỉ đọc `description` khi `html` rỗng.
   */
  text?: string
  kind: 'product' | 'post' | 'category'
  errors?: Record<string, string>
  settings?: SiteSettings | null
  docId?: string
  /** Dữ liệu riêng theo loại (ảnh, giá, brand, FAQ…) để chấm các mục rich result. */
  extra: Omit<SeoInput, 'kind' | 'focusKeyword' | 'secondaryKeywords' | 'metaTitle' | 'metaDescription' | 'hasMetaImage' | 'slug' | 'productTitle' | 'html' | 'description' | 'noindex' | 'canonical' | 'duplicateTitleWith' | 'duplicateKeywordWith' | 'siteHost'>
}) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [advanced, setAdvanced] = useState(false)
  const [conflicts, setConflicts] = useState<SeoConflicts | null>(null)
  const [openGroups, setOpenGroups] = useState<Set<SeoGroup>>(new Set())

  const siteUrl = settings?.siteUrl || 'https://tiemnhanho.vn'
  const host = siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
  const set = (patch: Partial<SeoValue>) => onChange({ ...value, ...patch })

  /** Title thật mà web sẽ render: ô SEO title, hoặc mẫu tiêu đề áp lên H1. */
  const effectiveTitle = value.title.trim() || renderTitleTemplate(settings?.titleTemplate ?? '%title% | %sitename%', {
    title: pageTitle, sitename: settings?.siteName ?? 'Nhà Nhỏ', separator: settings?.titleSeparator ?? '|',
  })
  const effectiveDesc = value.description.trim() || settings?.defaultMetaDescription || ''

  // Hỏi server về trùng lặp — chỉ khi người dùng ngưng gõ, và chỉ khi có gì để hỏi.
  const dTitle = useDebounced(effectiveTitle, 700)
  const dKeyword = useDebounced(value.focusKeyword.trim(), 700)
  useEffect(() => {
    if (!dTitle && !dKeyword) { setConflicts(null); return }
    let alive = true
    seoApi.conflicts({ kind, id: docId, title: dTitle, keyword: dKeyword })
      .then((r) => alive && setConflicts(r))
      .catch(() => alive && setConflicts(null))
    return () => { alive = false }
  }, [dTitle, dKeyword, kind, docId])

  const result = useMemo(() => analyzeSeo({
    kind,
    focusKeyword: value.focusKeyword,
    secondaryKeywords: value.secondaryKeywords,
    metaTitle: effectiveTitle,
    metaDescription: effectiveDesc,
    hasMetaImage: !!value.image || !!settings?.defaultOgImage,
    slug: slug.split('/').pop() ?? '',
    productTitle: pageTitle,
    html,
    description: text ?? '',
    noindex: value.noindex || settings?.allowIndexing === false,
    canonical: value.canonical || null,
    siteHost: host,
    duplicateTitleWith: conflicts?.duplicateTitle?.title ?? null,
    duplicateKeywordWith: conflicts?.duplicateKeyword?.title ?? null,
    ...extra,
  }), [kind, value, effectiveTitle, effectiveDesc, slug, pageTitle, html, text, host, conflicts, settings, extra])

  const byGroup = useMemo(() => {
    const m = new Map<SeoGroup, SeoCheck[]>()
    for (const c of result.checks) m.set(c.group, [...(m.get(c.group) ?? []), c])
    return [...m.entries()]
  }, [result])

  const st = result.stats
  const blockedBySite = settings?.allowIndexing === false

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <div className="space-y-5">
        {blockedBySite && (
          <p className="rounded-card-sm bg-danger-tint px-3 py-2 text-xs text-danger">
            Toàn site đang chặn Google (Cài đặt → Cho phép Google lập chỉ mục = tắt). Mọi tối ưu ở đây sẽ không có tác dụng cho tới khi bật lại.
          </p>
        )}

        <Field label="Từ khoá chính" hint='Cụm người mua thật sự gõ vào Google. Càng cụ thể càng dễ lên top: "móc khoá in 3d cá nhân hoá" thay vì "móc khoá".' error={errors['meta.focusKeyword']}>
          <Input value={value.focusKeyword} onChange={(e) => set({ focusKeyword: e.target.value })} placeholder="móc khoá in 3d cá nhân hoá" />
        </Field>
        {conflicts?.duplicateKeyword && (
          <p className="-mt-3 text-xs text-danger">
            Từ khoá này đang là từ khoá chính của "{conflicts.duplicateKeyword.title}". Hai trang cùng nhắm một từ khoá sẽ tự cạnh tranh nhau — đổi một bên sang từ khoá khác.
          </p>
        )}
        <Field label="Từ khoá phụ" hint="Các cụm dài liên quan, nên xuất hiện trong nội dung. Enter để thêm." error={errors['meta.secondaryKeywords']}>
          <TagInput value={value.secondaryKeywords} onChange={(secondaryKeywords) => set({ secondaryKeywords })} placeholder="quà tặng in 3d, móc khoá theo tên…" />
        </Field>

        <Field label="Tiêu đề trên Google" error={errors['meta.title']}
          hint={value.title.trim() ? undefined : `Để trống → dùng mẫu "${settings?.titleTemplate ?? '%title% | %sitename%'}"`}>
          <Input value={value.title} placeholder={effectiveTitle} maxLength={200} onChange={(e) => set({ title: e.target.value })} />
        </Field>
        <PixelMeter px={textPixels(effectiveTitle, 20)} min={SEO_LIMITS.titlePixels.min} max={SEO_LIMITS.titlePixels.max} />

        <Field label="Mô tả trên Google" error={errors['meta.description']}
          hint={`${value.description.length} ký tự · nhắm ${SEO_LIMITS.descChars.min}–${SEO_LIMITS.descChars.max}, có từ khoá và một lời kêu gọi hành động`}>
          <Textarea value={value.description} maxLength={500} onChange={(e) => set({ description: e.target.value })} placeholder="Móc khoá in 3D khắc tên theo yêu cầu, chất liệu PLA an toàn. Xem mẫu và đặt riêng hôm nay." />
        </Field>
        <PixelMeter px={textPixels(effectiveDesc, 14)} min={SEO_LIMITS.descPixels.min} max={SEO_LIMITS.descPixels.max} />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-ink">Xem trước trên Google</span>
            <div className="flex gap-1 text-xs">
              {(['desktop', 'mobile'] as const).map((d) => (
                <button key={d} type="button" onClick={() => setDevice(d)}
                  className={`rounded-button px-2 py-1 ${device === d ? 'bg-primary-100 font-medium text-primary-950' : 'text-ink-muted hover:bg-sand'}`}>
                  {d === 'desktop' ? 'Máy tính' : 'Điện thoại'}
                </button>
              ))}
            </div>
          </div>
          <SerpPreview title={effectiveTitle} description={effectiveDesc} url={`${host} › ${slug.replace(/\//g, ' › ')}`} device={device} />
        </div>

        <Field as="div" label="Ảnh chia sẻ (Open Graph)" hint="Hiện khi dán link lên Facebook/Zalo. Nên 1200×630." error={errors['meta.image']}>
          <ImageField value={value.image} onChange={(image) => set({ image })} />
        </Field>
        <SocialPreview image={value.image ?? (typeof settings?.defaultOgImage === 'object' ? settings.defaultOgImage : null)}
          title={value.ogTitle || effectiveTitle} description={value.ogDescription || effectiveDesc} host={host} />

        <div className="rounded-card-sm border border-border">
          <button type="button" onClick={() => setAdvanced((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-ink hover:bg-sand">
            <span>Nâng cao: robots, canonical, ghi đè mạng xã hội</span>
            <span className="text-ink-muted">{advanced ? '▲' : '▼'}</span>
          </button>
          {advanced && (
            <div className="space-y-4 border-t border-border p-4">
              <Toggle checked={value.noindex} onChange={(noindex) => set({ noindex })} label="noindex — chặn trang này khỏi Google" />
              {value.noindex && <p className="text-xs text-danger">Trang sẽ không bao giờ xuất hiện trên Google. Chỉ dùng cho trang trùng lặp hoặc nội bộ.</p>}
              <Toggle checked={value.nofollow} onChange={(nofollow) => set({ nofollow })} label="nofollow — không truyền uy tín qua link trên trang" />
              <Field label="Canonical" hint="Để trống là an toàn nhất (trang tự trỏ về chính nó). Chỉ điền URL đầy đủ khi đang cố tình gộp hai trang trùng nội dung." error={errors['meta.canonical']}>
                <Input value={value.canonical} onChange={(e) => set({ canonical: e.target.value })} placeholder="https://tiemnhanho.vn/san-pham/…" className="font-mono text-xs" />
              </Field>
              <Field label="Tiêu đề khi chia sẻ (og:title)" hint="Trống → dùng tiêu đề Google" error={errors['meta.ogTitle']}>
                <Input value={value.ogTitle} maxLength={200} onChange={(e) => set({ ogTitle: e.target.value })} placeholder={effectiveTitle} />
              </Field>
              <Field label="Mô tả khi chia sẻ (og:description)" hint="Trống → dùng mô tả Google. Mạng xã hội cho phép câu văn cảm xúc hơn." error={errors['meta.ogDescription']}>
                <Textarea value={value.ogDescription} maxLength={500} onChange={(e) => set({ ogDescription: e.target.value })} placeholder={effectiveDesc} />
              </Field>
              <JsonLdField value={value.jsonLd} onChange={(jsonLd) => set({ jsonLd })} error={errors['meta.jsonLd']} />
            </div>
          )}
        </div>
      </div>

      <aside className="space-y-4">
        <div className="rounded-card-sm border border-border bg-white p-4">
          <ScoreRing result={result} />
          {result.todos.length > 0 ? (
            <>
              <div className="mt-4 text-sm font-medium text-ink">Việc cần làm</div>
              <ul className="mt-2 space-y-2">
                {result.todos.slice(0, 6).map((c) => <CheckRow key={c.id} c={c} />)}
              </ul>
              {result.todos.length > 6 && <p className="mt-2 text-2xs text-ink-faint">… và {result.todos.length - 6} mục nữa ở danh sách dưới.</p>}
            </>
          ) : (
            <p className="mt-4 text-sm text-success">Mọi mục đều đạt ✓</p>
          )}
        </div>

        <div className="rounded-card-sm border border-border bg-white p-4">
          <div className="text-sm font-medium text-ink">Phân tích nội dung</div>
          <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-xs">
            <dt className="text-ink-muted">Số từ</dt><dd className="text-right text-ink">{st.words} ({st.readingMinutes} phút đọc)</dd>
            <dt className="text-ink-muted">Từ khoá xuất hiện</dt><dd className="text-right text-ink">{st.keywordCount} lần · {st.keywordDensity}%</dd>
            <dt className="text-ink-muted">Tiêu đề phụ</dt><dd className="text-right text-ink">{st.headings.filter((h) => h.level >= 2).length}</dd>
            <dt className="text-ink-muted">Link nội bộ / ra ngoài</dt><dd className="text-right text-ink">{st.internalLinks} / {st.externalLinks}</dd>
            <dt className="text-ink-muted">Ảnh thiếu alt</dt><dd className={`text-right ${st.imagesMissingAlt ? 'text-danger' : 'text-ink'}`}>{st.imagesMissingAlt}</dd>
            <dt className="text-ink-muted">Câu dài</dt><dd className="text-right text-ink">{st.longSentences}/{st.sentences}</dd>
          </dl>
          {st.headings.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-ink-muted">Cấu trúc tiêu đề</summary>
              <ul className="mt-1 space-y-0.5 text-2xs">
                {st.headings.map((h, i) => (
                  <li key={i} className={h.level === 1 ? 'text-danger' : 'text-ink-muted'} style={{ paddingLeft: (h.level - 1) * 8 }}>
                    H{h.level} · {h.text.slice(0, 60) || '(trống)'}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="rounded-card-sm border border-border bg-white">
          <div className="border-b border-border px-4 py-3 text-sm font-medium text-ink">Toàn bộ {result.checks.length} mục kiểm tra</div>
          {byGroup.map(([group, list]) => {
            const worst: CheckStatus = list.some((c) => c.status === 'bad') ? 'bad' : list.some((c) => c.status === 'warn') ? 'warn' : 'good'
            const open = openGroups.has(group)
            return (
              <div key={group} className="border-b border-border last:border-0">
                <button type="button"
                  onClick={() => setOpenGroups((s) => { const n = new Set(s); n.has(group) ? n.delete(group) : n.add(group); return n })}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs hover:bg-sand">
                  <span className={`size-2 shrink-0 rounded-full ${DOT[worst]}`} />
                  <span className="flex-1 text-ink">{SEO_GROUP_LABEL[group]}</span>
                  <span className="text-ink-faint">{list.filter((c) => c.status === 'good').length}/{list.length}</span>
                </button>
                {open && <ul className="space-y-2 px-4 pb-3">{list.map((c) => <CheckRow key={c.id} c={c} />)}</ul>}
              </div>
            )
          })}
        </div>
      </aside>
    </div>
  )
}
